/**
 * 脚本节点卡片内容（镜像 DirectorContent / LayerEditContent）
 * 空态：仅中央脚本标识图标 48px（尺寸对齐图片节点，「尝试」三入口由 GenerateNode 共享 TryActions 渲染，与图片节点完全同构）；
 * 生成中：仅中央文本图标的大空卡（参考图 在尝试点击剧本生成分镜脚本.png）；
 * 完成：脚本标识图标 48px + 三步骤条 + 「打开脚本节点 →」（参考图 生成后的节点状态.png）；
 * v1.4.83 三步骤打勾改为真实完成状态（用户反馈：生成脚本后第一步就该勾、全部图片生成后第二步勾、
 * 全部提示词合成后第三步勾）——判据与 ScriptPanel stats 同源，废除 done = i < currentIdx 位置式虚勾
 */
import React, { memo } from 'react';
import { FileText, Check } from 'lucide-react';
import { AppNode, ScriptData, ScriptStep } from '@/types';
import { readScript } from '@/types/scriptNode';

interface ScriptContentProps {
  data: AppNode;
  isWorking: boolean;
  onOpenScript?: (nodeId: string) => void;
  /** 待生成态手动触发生成（剧本/角色入口创建上游节点后，由卡片按钮触发） */
  onGenerate?: (nodeId: string, prompt?: string) => void;
}

const STEP_ORDER: ScriptStep[] = ['SHOTS', 'ASSETS', 'PROMPTS'];
const STEP_TEXTS: Record<ScriptStep, string> = {
  SHOTS: '确认镜头',
  ASSETS: '准备资产',
  PROMPTS: '合成提示词',
};

/** 三步骤横向进度（圆圈在上、文字在下；打勾=真实完成状态（v1.4.83，与面板 stats 同源）：
 * 确认镜头=已产出分镜（生成脚本完成即勾）/ 准备资产=全部资产图生成成功 / 合成提示词=全部镜头提示词合成成功；
 * 此前 done = i < currentIdx 纯位置式——停在第一步时首步永不勾、自由跳页后前序步骤全虚勾） */
const StepsInline: React.FC<{ script: ScriptData }> = memo(({ script }) => {
  const currentIdx = Math.max(0, STEP_ORDER.indexOf(script.currentStep));
  // v1.4.83 真实完成状态（判据与 ScriptPanel stats.step1-3Done 完全一致）
  const stepDone: Record<ScriptStep, boolean> = {
    SHOTS: script.shots.length > 0,
    ASSETS: script.assets.length > 0 && script.assets.every((a) => a.status === 'SUCCESS'),
    PROMPTS: script.shots.length > 0 && script.shots.every((s) => s.promptStatus === 'SUCCESS'),
  };
  return (
    <div className="flex items-start">
      {STEP_ORDER.map((step, i) => {
        const done = stepDone[step];
        const active = !done && i === currentIdx;
        return (
          <React.Fragment key={step}>
            {i > 0 && (
              <div className={`h-[2px] w-12 mt-[15px] mx-3 transition-colors ${i <= currentIdx ? 'bg-white/60' : 'bg-white/15'}`} />
            )}
            <div className="flex flex-col items-center gap-2 min-w-[76px]">
              <div
                className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-[14px] transition-colors ${
                  done
                    ? 'bg-white border-white text-zinc-900'
                    : active
                      ? 'border-white text-white bg-transparent'
                      : 'border-white/25 text-white/35'
                }`}
              >
                {done ? <Check size={16} strokeWidth={3} /> : i + 1}
              </div>
              <span className={`text-[16px] whitespace-nowrap ${active || done ? 'text-white/85' : 'text-white/35'}`}>
                {STEP_TEXTS[step]}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
});

const ScriptContentComponent: React.FC<ScriptContentProps> = memo(({
  data,
  isWorking,
  onOpenScript,
  onGenerate,
}) => {
  const script = readScript((data.data as { script?: unknown }).script);
  // 完成态判定：手动编写空脚本也算（用户可直接加镜头）；生成来源需已产出镜头
  const hasContent = !!script && (script.source === 'MANUAL' || script.shots.length > 0);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onOpenScript?.(data.id);
  };

  // 生成中：大空卡仅保留中央文本图标（无步骤条无按钮）
  if (isWorking) {
    return (
      <div className="flex flex-col items-center justify-center h-full">
        <FileText size={96} strokeWidth={1.2} className="text-white/40" />
      </div>
    );
  }

  // 完成：中央脚本节点标识图标（与空态同源，尺寸对齐图片节点 48px） + 三步骤条 + 底部「打开脚本节点 →」（节点标题由 NodeTitle 渲染在卡片外上方）；
  // v1.4.56 资产生成中时按钮上方追加进度提示（画布资产节点由 useScriptAssetGenSync 回写 script.assets.progress）
  if (script && hasContent) {
    const hasShots = script.shots.length > 0;
    const genAsset = script.assets.find((a) => a.status === 'WORKING');
    return (
      <div className="relative flex flex-col h-full px-16 pt-10 pb-12" onDoubleClick={handleOpen}>
        <div className="flex-1 flex flex-col items-center justify-center gap-14">
          <img src="/icons/node-picker/script.svg" alt="" width={48} height={48} draggable={false} />
          <StepsInline script={script} />
        </div>
        {genAsset && (
          <div className="mb-5 self-center text-[13px] text-white/60 animate-pulse whitespace-nowrap">
            资产{genAsset.progress || '生成中...'}
          </div>
        )}
        <button
          type="button"
          onClick={handleOpen}
          onMouseDown={(e) => e.stopPropagation()}
          className={`self-center px-12 h-14 rounded-xl text-[18px] font-medium transition-colors ${
            hasShots
              ? 'bg-white/[0.12] text-white hover:bg-white/20'
              : 'bg-white text-zinc-900 hover:bg-white/90'
          }`}
        >
          打开脚本节点 →
        </button>
      </div>
    );
  }

  // 空态：仅保留中央脚本图标（「尝试」入口由 GenerateNode 共享 TryActions 渲染在卡片左下，样式与图片节点完全一致）
  return (
    <div className="relative h-full">
      <div className="absolute inset-0 flex items-center justify-center">
        <img src="/icons/node-picker/script.svg" alt="" width={48} height={48} draggable={false} />
      </div>
    </div>
  );
});

ScriptContentComponent.displayName = 'ScriptContent';
export const ScriptContent = ScriptContentComponent;
